/**
 * State du wizard de creation de personnage.
 *
 * 8 etapes :
 * 1. Identite (nom, concept, ideaux, defauts, phobies)
 * 2. Profil (choix parmi 14)
 * 3. Peuple (choix parmi 8 + modificateurs)
 * 4. Caracs (distribution serie + modifs peuple)
 * 5. Traits physiques (age, taille, poids)
 * 6. Voies (choix de 2 voies parmi celles du profil, rang 1 + rang 2 mage)
 * 7. Equipement (attribue auto + langues)
 * 8. Recap + creation
 */

import { CARACS, CARAC_SERIES, type Carac, type CaracSerieId, emptyCaracs } from '@domain/caracs';
import type { PeupleChoiceResolution } from '@domain/peuple';
import { signal } from './signal';

export type WizardStep = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface WizardState {
  step: WizardStep;
  nom: string;
  concept: string;
  ideaux: string[];
  defauts: string[];
  phobies: string[];
  secretsIntimes: string[];
  talentsSecondaires: string[];
  profilId: string | null;
  isHybride: boolean;
  profilSecondaireId: string | null;
  profilPrimaireId: string | null;
  peupleId: string | null;
  peupleChoices: PeupleChoiceResolution | null;
  caracSerie: CaracSerieId | null;
  caracAssignment: Record<Carac, number>;
  peupleModBonusChoices: string[];
  peupleModMalusChoices: string[];
  humainBonusCarac: Carac | null;
  age: number | null;
  taille: number | null;
  poids: number | null;
  voiesChoisies: string[];
  voiePeupleId: string | null;
  useVoieMage: boolean;
  voieRang2: string | null;
  diviniteId: string | null;
  choixVoies: Record<string, string>;
  equipChoices: Record<number, string>;
  languesSupp: string[];
}

function initialState(): WizardState {
  return {
    step: 1,
    nom: '',
    concept: '',
    ideaux: [],
    defauts: [],
    phobies: [],
    secretsIntimes: [],
    talentsSecondaires: [],
    profilId: null,
    isHybride: false,
    profilSecondaireId: null,
    profilPrimaireId: null,
    peupleId: null,
    peupleChoices: null,
    caracSerie: null,
    caracAssignment: emptyCaracs(),
    peupleModBonusChoices: [],
    peupleModMalusChoices: [],
    humainBonusCarac: null,
    age: null,
    taille: null,
    poids: null,
    voiesChoisies: [],
    voiePeupleId: null,
    useVoieMage: false,
    voieRang2: null,
    diviniteId: null,
    choixVoies: {},
    equipChoices: {},
    languesSupp: [],
  };
}

export const wizardState = signal<WizardState>(initialState());

export function wizardReset(): void {
  wizardState.set(initialState());
}

export function wizardUpdate(partial: Partial<WizardState>): void {
  wizardState.set({ ...wizardState.get(), ...partial });
}

export function wizardNext(): void {
  const s = wizardState.get();
  if (s.step < 7) wizardState.set({ ...s, step: (s.step + 1) as WizardStep });
}

export function wizardPrev(): void {
  const s = wizardState.get();
  if (s.step <= 1) return;
  const prev = (s.step - 1) as WizardStep;
  const resets: Partial<WizardState> = {};
  if (prev <= 1) {
    resets.profilId = null;
    resets.isHybride = false;
    resets.profilSecondaireId = null;
    resets.profilPrimaireId = null;
  }
  if (prev <= 2) {
    resets.peupleId = null;
    resets.voiePeupleId = null;
    resets.peupleModBonusChoices = [];
    resets.peupleModMalusChoices = [];
    resets.humainBonusCarac = null;
  }
  if (prev <= 3) {
    resets.caracSerie = null;
    resets.caracAssignment = emptyCaracs();
  }
  if (prev <= 4) {
    resets.age = null;
    resets.taille = null;
    resets.poids = null;
  }
  if (prev <= 5) {
    resets.voiesChoisies = [];
    resets.useVoieMage = false;
    resets.diviniteId = null;
    resets.choixVoies = {};
    resets.voieRang2 = null;
  }
  if (prev <= 6) {
    resets.equipChoices = {};
  }
  wizardState.set({ ...s, step: prev, ...resets });
}

export function isCaracAssignmentValid(state: WizardState): boolean {
  if (!state.caracSerie) return false;
  const serie = [...CARAC_SERIES[state.caracSerie]].sort((a, b) => a - b);
  const assigned = CARACS.map((k) => state.caracAssignment[k]).sort((a, b) => a - b);
  return serie.length === assigned.length && serie.every((v, i) => v === assigned[i]);
}

export function isStepValid(state: WizardState, step: WizardStep): boolean {
  switch (step) {
    case 1:
      return (
        state.nom.trim().length >= 1 &&
        state.ideaux.length >= 1 &&
        state.defauts.length >= 1 &&
        state.phobies.length >= 1 &&
        state.secretsIntimes.length >= 1
      );
    case 2:
      if (state.isHybride) {
        return (
          state.profilId !== null &&
          state.profilSecondaireId !== null &&
          state.profilPrimaireId !== null &&
          state.profilId !== state.profilSecondaireId
        );
      }
      return state.profilId !== null;
    case 3:
      return state.peupleId !== null;
    case 4:
      return isCaracAssignmentValid(state);
    case 5:
      return state.age !== null && state.taille !== null && state.poids !== null;
    case 6: {
      const hasVoiePeuple = state.voiePeupleId !== null;
      const voiesOk =
        state.voiesChoisies.length === 2 || (state.voiesChoisies.length === 1 && state.useVoieMage);
      const rang2Ok = !state.useVoieMage || state.voieRang2 !== null;
      return voiesOk && hasVoiePeuple && rang2Ok;
    }
    case 7: {
      const intSlots = Math.max(0, state.caracAssignment.INT);
      const used = state.languesSupp.length + state.talentsSecondaires.length;
      return used >= intSlots;
    }
  }
}
